<script setup>
import { Field, ErrorMessage, useField } from 'vee-validate';
import { ref, watch, inject, computed } from 'vue';
import { isArray } from 'lodash';
import { vMaska } from 'maska/vue';
import { __, isNumber, getFormValuesFromLocalStorage, requestAvailableCities, requestAvailableStreets, decodeHTMLEntities } from '@/utils';
import Modal from "@components/modal.vue";
import OtpCode from "@components/otp-code.vue";
import Loader from '@components/loader.vue';

const props = defineProps(['name', 'fieldPhoneNumber']);
const emit = defineEmits(['selectedNumber'])

const modalError = ref(false);
const savedFields = getFormValuesFromLocalStorage();
const showOtpModal = ref(false);
const loader = ref(false);
const ID = ref(savedFields[`${props.name}ID`] ? savedFields[`${props.name}ID`] : undefined)
const otpCode = ref('');
const injectNumber = ref('');
const MINUTES = 5;
const timer = ref(localStorage.getItem('otpTimer') || null);
const interval = ref(null);
const currentTimer = ref(null);

const {
    subscriberDisableExistingFields = { value: "" },
	companyDisableExistingFields,
	isExistingAccount,
	isPOAddress
} = inject('step2') || {};

const disableIDField = ref(props.name === 'subscriber' ? subscriberDisableExistingFields.value : companyDisableExistingFields.value );
const isSubscriber = computed(() => props.name === 'subscriber' ? 'field--w160' : '')


const { handleChange: setAccountId } = useField('account_id');
const { handleChange: setFirstName } = useField(`${props.name}FirstName`);
const { handleChange: setLastName } = useField(`${props.name}LastName`);
const { handleChange: setApartment} = useField(`${props.name}Apartment`);
const { handleChange: setFloor} = useField(`${props.name}Floor`);
const { handleChange: setNumberHome} = useField(`${props.name}NumberHome`);
const { handleChange: setEntrance} = useField(`${props.name}Entrance`);
const { handleChange: setSettlement} = useField(`${props.name}Settlement`);
const { handleChange: setStreet} = useField(`${props.name}Street`);
const { handleChange: setInvoice } = useField('selectedInvoice');
const { handleChange: setEmail} = useField(`${props.name}Email`);
const { handleChange: setCityCode} = useField(`${props.name}CityCode`);
const { handleChange: setStreetCode} = useField(`${props.name}StreetCode`);
const { handleChange: subFields} = useField('subscriberDisableExistingFields');
const { handleChange: compFields} = useField('companyDisableExistingFields');
const { handleChange: paymentGateway} = useField('paymentGateway');
const { handleChange: existingAccount} = useField('existingAccount');
const { handleChange: handleZipCode} = useField('zipCode');
const { handleChange: setByPO} = useField(`${props.name}AddressType`);
const { handleChange: setPO} = useField(`${props.name}PO`);
const { handleChange: setPOCity} = useField(`${props.name}POSettlement`);

const setTimer = () => {
    const now = new Date();
    const fiveMinutesLater = new Date(now.getTime() + MINUTES * 60000);
    timer.value = fiveMinutesLater;
    localStorage.setItem('otpTimer', fiveMinutesLater);
}

const timerCountDown = () => {
    if(!localStorage.getItem('otpTimer')) {
        return;
    }
    const now = new Date();
    const timerValue = new Date(timer.value);
    const diff = timerValue - now;
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (diff < 0) {
        localStorage.removeItem('otpTimer');
        clearInterval(interval.value);
        currentTimer.value = null;
        return '00:00';
    }

    currentTimer.value = `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
}

const initTimer = () => {
    if (timer.value) {
        timerCountDown();
        interval.value = setInterval(() => {
            timerCountDown();
        }, 1000);
    }
}


const validateOTP = async () => {
	loader.value = true;
	modalError.value = false;

	let response;
	try {
		response = await checkAccount(ID.value, otpCode.value);
	} catch (error) {
		console.error('Error:', error)
		return;
	}

	const responseData = JSON.parse(response.data)

	if (responseData.response?.id) {
		const userObject = responseData.response;

		if (props.name == 'subscriber') {
			subscriberDisableExistingFields.value = true;
			subFields(true);
		}else{
			companyDisableExistingFields.value = true;
			compFields(true);
		}

		const firstName = decodeHTMLEntities(userObject?.first_name);
		const lasttName = decodeHTMLEntities(userObject?.last_name);
		const responseSettlemnt = decodeHTMLEntities(userObject?.address?.city);
		const responseStreet = decodeHTMLEntities(userObject?.address?.street);

		isExistingAccount.value = true;
		existingAccount(true);
		setAccountId(userObject?.id);
		setFirstName(firstName);
		setLastName(lasttName);
		setApartment(userObject?.address?.apartment_number);
		setFloor(userObject?.address?.floor);
		setNumberHome(userObject?.address?.house_number);
		setEntrance(userObject?.address?.house_enter);
		setSettlement(responseSettlemnt);
		setStreet(responseStreet);
		setInvoice(userObject?.invoice_shipping_method)
		setEmail(userObject?.email)
		paymentGateway(userObject?.payment_gateway)
		setCityCode(userObject?.address?.city_code)
		setStreetCode(userObject?.address?.street_code)
		handleZipCode(userObject?.address?.zip_code)
		
		if(userObject?.address?.mailbox){
			isPOAddress.value = 'byPO';
			setByPO('byPO');
			setPO(userObject?.address?.mailbox)
			setPOCity(responseSettlemnt)
		}
		
		
		if (userObject?.phone){
			emit('selectedNumber', userObject.phone);
		}
		showOtpModal.value = false;
	} else {
		modalError.value = true;
	}

	loader.value = false
}


const checkAccount = async (userID, smsValidation = null) => {
	const smsCode = smsValidation ? `&smsValidation=${smsValidation}` : '';
	const response = await fetch(ajax_object.ajax_url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body: `action=check_existing_account&userID=${userID}${smsCode}`,
	}).then(response => response.json())

	return response;
}

const handleExitingAccountCheck = async (value) => {
	let response;
	try {
		response = await checkAccount(value);
	} catch (error) {
		console.error('Error:', error)
		return;
	}

	const responseData = JSON.parse(response.data)
	if(!responseData.response.status && responseData.response.error === "required_otp") {
		const responseText = JSON.parse(responseData.response['response-text']);
		injectNumber.value = responseText.phone
		showOtpModal.value = true;
	}
}

const resendOTP = () => {
	checkAccount(ID.value);
	setTimer();
    initTimer();
}


watch(ID, (value) => {
	if (value.length === 9) {
		handleExitingAccountCheck(value);
	}
});

watch(subscriberDisableExistingFields, (value) => {
	disableIDField.value = subscriberDisableExistingFields.value
});

watch(companyDisableExistingFields, (value) => {
	disableIDField.value = companyDisableExistingFields.value
});

if(timer.value) {
    initTimer();
}

const cancleOTP = () => {
	showOtpModal.value = false;
	ID.value = '';
}
</script>
<template>
    <div :class="`field ${isSubscriber}`">
        <ErrorMessage :name="`${name}ID`" as="span" class="field__error" />

        <label for="field-id">
            <strong>{{ __('ID') }} *</strong>
        </label><!-- /.form__label -->

        <Field
			type="text"
			:name="`${props.name}ID`"
			id="field-id"
            @keypress="isNumber($event)"
			maxlength="9" v-model="ID"
			:disabled="disableIDField"
			v-maska
			data-maska="#########"
		/>
    </div><!-- /.field -->

	<Modal :isActive="showOtpModal" @popup_x="cancleOTP">
		<template #head>
			<h6>
				{{ __('Wait... we already know each other') }}
			</h6>
		</template>

		<template #body>
			<div class="notice-verification">
				<div class="notice__body">
					<p>
						{{ __('The ID number is already recognized in the system, the numbers you selected will be added as an additional subscription to your account') }}
					</p>

					<p>
						<strong>
							{{ __('SMS with a verification code will be sent to the number') }} - {{ injectNumber }}<br/>
							{{ __('Enter the 4 digits you received here') }}
						</strong>
					</p>
				</div><!-- /.notice__body -->

				<div class="notice__field">
					<div class="field-code":class="{ 'field-code__small-gap': modalError }">
						<div class="field__controls">
							<OtpCode @changed="otpCode = $event" :error="modalError" />
						</div><!-- /.field__controls -->
						<p v-if="modalError" style="color: #E50000;">
							{{ __('Incorrect code entered. Please try again.') }}
						</p>

						<p>
							{{ __("Didn't receive a code?") }}
						
							<button class="link-underline" @click="resendOTP" type="button" :disabled="loader || currentTimer">
								{{ __('Click here to send it again') }} 
								<template v-if="currentTimer">
									<small>{{ currentTimer }}</small>
								</template>
							</button>
						</p>
					</div><!-- /.field-code -->
				</div><!-- /.notice__field -->

				<div class="notice__actions">
					<button class="notice__btn btn" type="button" @click="validateOTP" :disabled="otpCode.length < 4 || loader">
						{{ __('to complete the process') }} <Loader v-if="loader" />
					</button><!-- /.btn -->

					<button class="link-underline" type="button" @click="cancleOTP">
						{{ __('Cancel') }}
					</button><!-- /.link-underline -->
				</div><!-- /.notice__actions -->
			</div><!-- /.notice-verification -->
		</template>
	</Modal>
</template>
